import {
  Listbox,
  ListboxButton,
  ListboxLabel,
  ListboxOption,
  ListboxOptions,
  Transition,
} from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import clsx from 'clsx';
import { Fragment, useState, useRef, useEffect, useMemo } from 'react';
import { useFormContext, Controller } from 'react-hook-form';

// Base required properties
export type BaseDropdownOption = {
  id: string | number;
  label: string;
  value: string;
  strike?: boolean;
};

// Generic type that extends the base option
export type DropdownOption<T> = BaseDropdownOption & T;

type DropdownVariant = 'full' | 'auto' | 'none';
type LabelPosition = 'top' | 'inline';

interface DropdownProps<T> {
  label?: string;
  labelPosition?: LabelPosition;
  options: DropdownOption<T>[];
  value?: DropdownOption<T> | null;
  onChange?: (option: DropdownOption<T> | null) => void;
  required?: boolean;
  placeholder?: string;
  className?: string;
  error?: string;
  variant?: DropdownVariant;
  name: string;
  disabled?: boolean;
  isLoading?: boolean;
}

export function Dropdown<T>({
  label,
  labelPosition = 'top',
  options,
  value: propValue,
  onChange: propOnChange,
  required = false,
  placeholder = 'Select',
  className,
  error: propError,
  variant = 'full',
  name,
  disabled = false,
  isLoading = false,
}: DropdownProps<T>): JSX.Element {
  const formContext = useFormContext();
  const error =
    propError || (formContext?.formState?.errors[name]?.message as string);

  const [filteredOptions, setFilteredOptions] = useState(options);
  const [isOpen, setIsOpen] = useState(false);
  const optionsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setFilteredOptions(options);
  }, [options]);

  const handleInputChange = (inputValue: string): void => {
    const newFilteredOptions = options.filter((option) =>
      option.label.toLowerCase().includes(inputValue.toLowerCase()),
    );
    setFilteredOptions(newFilteredOptions);
  };

  const handleOptionSelect = (
    selectedOption: DropdownOption<T> | null,
  ): void => {
    propOnChange?.(selectedOption);
    setIsOpen(false);
  };

  const displayOptions = useMemo(() => {
    if (isLoading) {
      return [
        {
          id: 'loading',
          label: 'Loading...',
          value: 'loading',
          strike: false,
        },
      ];
    }
    return filteredOptions;
  }, [isLoading, filteredOptions]);

  const renderDropdown = (controlledProps?: {
    onChange: (value: DropdownOption<T> | null) => void;
    value: DropdownOption<T> | null;
  }): JSX.Element => {
    return (
      <Listbox
        value={controlledProps?.value ?? propValue ?? null}
        onChange={(newValue) => {
          controlledProps?.onChange?.(newValue);
          handleOptionSelect(newValue);
        }}
        disabled={disabled || isLoading}
      >
        {() => (
          <div
            className={clsx(
              'relative flex gap-2',
              labelPosition === 'inline'
                ? 'flex-row items-center'
                : 'flex-col items-start',
              {
                'w-full': variant === 'full',
                'w-[200px]': variant === 'auto',
              },
            )}
          >
            {label && (
              <div className="flex items-center gap-1 whitespace-nowrap">
                <ListboxLabel
                  className={clsx(
                    'text-gray-600 text-md font-normal leading-6',
                  )}
                >
                  {label}
                </ListboxLabel>
                {required && (
                  <span className="text-[#CE3F4F] font-nunito text-sm font-normal leading-6">
                    *
                  </span>
                )}
              </div>
            )}

            <div
              className={clsx('relative', {
                'w-full': variant === 'full',
                'w-[200px]': variant === 'auto',
              })}
            >
              <div
                className={clsx(
                  'relative cursor-default py-2.5 pl-3  text-left',
                  'rounded-[6px] border border-[#D9D9D9] bg-[#FEFDFC]',
                  'w-full min-w-0 flex-shrink-0 h-10 flex items-center justify-between',
                  error ? 'border-red-500' : 'border-[#D9D9D9]',
                  disabled &&
                    'cursor-not-allowed bg-[var(--Color-Neutral-Fill-colorFillTertiary,rgba(0,0,0,0.04))] text-[#8C8C8C] font-roboto font-light',
                  'outline-none',
                )}
              >
                <div className="w-full min-w-0 overflow-hidden flex-shrink-0 flex items-center justify-between">
                  <input
                    type="text"
                    className="w-full outline-none"
                    value={
                      isOpen
                        ? undefined
                        : (
                            controlledProps?.value ?? propValue
                          )?.label?.trim() || ''
                    }
                    onFocus={() => setIsOpen(true)}
                    onBlur={() => {
                      setTimeout(() => {
                        setIsOpen(false);
                      }, 200); // delay to allow the options to close
                    }}
                    placeholder={placeholder}
                    onChange={(e) => {
                      const inputValue = e.target.value;
                      handleInputChange(inputValue);
                      if (!isOpen) {
                        const selectedOption = options.find(
                          (option) => option.label === inputValue,
                        );
                        if (selectedOption) {
                          handleOptionSelect(selectedOption);
                        }
                      }
                    }}
                    disabled={disabled}
                  />
                  <ListboxButton
                    className="pointer-events-auto flex items-center pr-2"
                    disabled={disabled}
                    onClick={() => setIsOpen(!isOpen)}
                  >
                    <ChevronDownIcon
                      className={clsx(
                        'h-5 w-5 flex-shrink-0 transition-transform duration-200',
                        isOpen && 'rotate-180',
                      )}
                      aria-hidden="true"
                    />
                  </ListboxButton>
                </div>
              </div>

              <Transition
                show={isOpen}
                as={Fragment}
                leave="transition ease-in duration-100"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <div ref={optionsRef}>
                  <ListboxOptions
                    className={clsx(
                      'absolute z-10 mt-1 max-h-40 overflow-auto',
                      'rounded-md bg-white py-1 shadow-lg',
                      'ring-1 ring-black ring-opacity-5',
                      'w-full',
                      'custom-scrollbar',
                    )}
                  >
                    {displayOptions.length > 0 ? (
                      displayOptions.map((option) => (
                        <ListboxOption
                          key={option.id}
                          className={clsx(
                            'relative cursor-pointer select-none py-2 pl-3 pr-9 font-roboto text-base leading-6 text-black',
                            'hover:bg-gray-100',
                            (option?.strike && 'line-through') as string,
                          )}
                          value={option}
                        >
                          {({ selected }) => (
                            <span
                              className={clsx(
                                'line-clamp-1',
                                selected ? 'font-medium' : 'font-normal',
                              )}
                            >
                              {option.label}
                            </span>
                          )}
                        </ListboxOption>
                      ))
                    ) : (
                      <div className="py-2 pl-3 pr-9 font-roboto text-base leading-6 text-gray-500">
                        No matches found
                      </div>
                    )}
                  </ListboxOptions>
                </div>
              </Transition>
            </div>
            {error && <p className="mt-1 text-sm text-red-500">{error}</p>}
          </div>
        )}
      </Listbox>
    );
  };
  return (
    <div
      className={clsx(
        'text',
        'flex ',
        labelPosition === 'inline'
          ? 'flex-row items-center'
          : 'flex-col items-start',
        'gap-2',
        {
          'w-full': variant === 'full',
          'w-[200px]': variant === 'auto',
        },
        className,
      )}
    >
      {formContext ? (
        <Controller
          name={name}
          control={formContext.control}
          render={({ field }) =>
            renderDropdown(
              field as {
                onChange: (value: DropdownOption<T> | null) => void;
                value: DropdownOption<T> | null;
              },
            )
          }
        />
      ) : (
        renderDropdown()
      )}
    </div>
  );
}
